import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  ArrowRight,
  ArrowUpRight,
  Clock3,
  Ellipsis,
  GitBranch,
  MessageCircle,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Workflow,
} from "lucide-react"
import { toast } from "sonner"
import type { AutomationDetail, AutomationSummary, UpdateAutomationInput } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AutoHeading, Choice, DotBadge, Note } from "@/components/automations/primitives"
import { AutomationsNav } from "@/components/automations/section-nav"
import { providerOptions } from "@/components/brand/account-options"
import { api, ApiError } from "@/lib/api"
import { relativeTime } from "@/lib/format"
import { automationsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"
import { cn } from "@/lib/utils"
import "@/styles/automation-polish.css"

export const Route = createFileRoute("/w/$workspace/automations/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(automationsQuery(params.workspace)),
  component: AutomationList,
})

const ICONS: Record<string, typeof MessageCircle> = {
  comment_to_dm: MessageCircle,
  welcome: Send,
  route: GitBranch,
  qualify: Workflow,
  after_hours: Clock3,
  blank: ShieldCheck,
}

function statusLabel(a: AutomationSummary) {
  return a.blocked
    ? "Blocked"
    : a.status === "published"
      ? "Published"
      : a.status === "paused"
        ? "Paused"
        : "Draft"
}

/** Screen 15. */
function AutomationList() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { automations } = useSuspenseQuery(automationsQuery(workspace.slug)).data
  const published = automations.filter((a) => a.status === "published" && !a.blocked).length
  const drafts = automations.filter((a) => a.draftVersion !== null).length
  const recentRuns = automations.reduce((total, a) => total + a.runsLast7Days, 0)
  const [query, setQuery] = useState("")
  const [tab, setTab] = useState("all")
  const [channel, setChannel] = useState("all")
  const params = { workspace: workspace.slug }
  const filtered = automations.filter(
    (a) =>
      a.name.toLowerCase().includes(query.toLowerCase()) &&
      (channel === "all" || a.provider === channel) &&
      (tab === "all" || statusLabel(a).toLowerCase() === tab)
  )
  const [deleting, setDeleting] = useState<AutomationSummary | null>(null)
  const [confirmText, setConfirmText] = useState("")
  const [renaming, setRenaming] = useState<AutomationSummary | null>(null)
  const [details, setDetails] = useState<{ name: string; description: string }>({
    name: "",
    description: "",
  })
  const rename = useMutation({
    mutationFn: ({ id, ...input }: UpdateAutomationInput & { id: string }) =>
      api<AutomationDetail>(`/w/${workspace.slug}/automations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: async (d) => {
      await queryClient.invalidateQueries({
        queryKey: ["workspace", workspace.slug, "automations"],
      })
      setRenaming(null)
      toast.success(`Saved as ${d.name}`)
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError ? error.problem.title : "Could not rename the automation"
      ),
  })
  const duplicate = useMutation({
    mutationFn: (a: AutomationSummary) =>
      api<AutomationDetail>(`/w/${workspace.slug}/automations/${a.id}/duplicate`, {
        method: "POST",
      }),
    onSuccess: async (copy) => {
      await queryClient.invalidateQueries({
        queryKey: ["workspace", workspace.slug, "automations"],
      })
      toast.success(`${copy.name} created as a draft`)
      await navigate({
        to: "/w/$workspace/automations/$automationId",
        params: { ...params, automationId: copy.id },
      })
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError ? error.problem.title : "Could not duplicate the automation"
      ),
  })
  const remove = useMutation({
    mutationFn: (a: AutomationSummary) =>
      api<null>(`/w/${workspace.slug}/automations/${a.id}`, { method: "DELETE" }),
    onSuccess: async (_r, a) => {
      await queryClient.invalidateQueries({
        queryKey: ["workspace", workspace.slug, "automations"],
      })
      setDeleting(null)
      setConfirmText("")
      toast.success(`${a.name} deleted`)
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError ? error.problem.title : "Could not delete the automation"
      ),
  })
  const toggle = useMutation({
    mutationFn: (a: AutomationSummary) =>
      api<AutomationDetail>(`/w/${workspace.slug}/automations/${a.id}/status`, {
        method: "POST",
        body: JSON.stringify({ status: a.status === "paused" ? "published" : "paused" }),
      }),
    onSuccess: async (d) => {
      await queryClient.invalidateQueries({
        queryKey: ["workspace", workspace.slug, "automations"],
      })
      toast.success(d.status === "paused" ? `${d.name} paused for new runs` : `${d.name} resumed`)
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError ? error.problem.title : "Could not update the automation"
      ),
  })
  return (
    <div className="page-wrap auto-page auto-list-polished">
      <AutoHeading
        title="Automations"
        description="Journeys reply and route conversations; moderation keeps comments in order; logs show what ran."
      >
        {can("edit_automations") && (
          <Button asChild>
            <Link to="/w/$workspace/automations/templates" params={params}>
              <Plus />
              Create automation
            </Link>
          </Button>
        )}
      </AutoHeading>
      <AutomationsNav workspace={workspace.slug} />
      <div className="auto-portfolio" aria-label="Automation overview">
        {[
          {
            label: "Live journeys",
            value: published,
            detail: "Published and ready for new runs",
            Icon: Workflow,
            featured: true,
          },
          {
            label: "Drafts in progress",
            value: drafts,
            detail: "Ready for your next idea",
            Icon: GitBranch,
            featured: false,
          },
          {
            label: "Runs this week",
            value: recentRuns,
            detail: "Across all journeys · last 7 days",
            Icon: Send,
            featured: false,
          },
        ].map(({ label, value, detail, Icon, featured }) => (
          <div key={label} className={cn("auto-portfolio-card", featured && "is-featured")}>
            <div>
              <p>{label}</p>
              <strong>{value.toLocaleString()}</strong>
              <small>{detail}</small>
            </div>
            <span className="auto-portfolio-icon" aria-hidden="true">
              <Icon />
            </span>
          </div>
        ))}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="auto-toolbar">
          <TabsList>
            {[
              ["all", "All automations"],
              ["published", "Published"],
              ["draft", "Draft"],
              ["paused", "Paused"],
            ].map(([v, l]) => (
              <TabsTrigger key={v} value={v!}>
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
          <Button variant="ghost" asChild>
            <Link to="/w/$workspace/automations/templates" params={params}>
              Browse templates
              <ArrowUpRight />
            </Link>
          </Button>
        </div>
      </Tabs>
      <div className="auto-toolbar my-4">
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute top-2 left-2.5 size-4 text-muted-foreground" />
            <Input
              className="pl-9 sm:w-72"
              aria-label="Search automations"
              placeholder="Search automations…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Choice
            label="Filter channel"
            value={channel}
            onChange={setChannel}
            values={providerOptions([
              { value: "all", label: "All channels" },
              { value: "instagram", label: "Instagram", provider: "instagram" },
              { value: "messenger", label: "Messenger", provider: "messenger" },
            ])}
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {filtered.length} automation{filtered.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="auto-table-surface overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5">Automation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Runs · 7 days</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((a) => {
              const Icon = ICONS[a.templateKey ?? "blank"] ?? Workflow
              const label = statusLabel(a)
              return (
                <TableRow key={a.id}>
                  <TableCell className="py-4 pl-5">
                    <div className="flex items-center gap-3">
                      <span className="auto-list-symbol" data-template={a.templateKey ?? "blank"}>
                        <Icon className="size-4" />
                      </span>
                      <div>
                        <Link
                          className="text-left font-medium hover:underline"
                          to={
                            a.blocked
                              ? "/w/$workspace/automations/$automationId/validate"
                              : "/w/$workspace/automations/$automationId"
                          }
                          params={{ ...params, automationId: a.id }}
                        >
                          {a.name}
                        </Link>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {a.provider === "instagram"
                            ? "Instagram"
                            : a.provider === "messenger"
                              ? "Messenger"
                              : "No channel"}{" "}
                          ·{" "}
                          {a.description ||
                            (a.blocked ? "Connection needs attention" : "Custom journey")}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <DotBadge warn={a.blocked}>{label}</DotBadge>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {a.liveVersion ? a.runsLast7Days : "—"}
                  </TableCell>
                  <TableCell>
                    <div>{a.liveVersion ? `v${a.liveVersion}` : "—"}</div>
                    {a.draftVersion && (
                      <span className="text-xs text-muted-foreground">Draft v{a.draftVersion}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {relativeTime(a.updatedAt)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label={`Actions for ${a.name}`}>
                          <Ellipsis />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link
                            to="/w/$workspace/automations/$automationId"
                            params={{ ...params, automationId: a.id }}
                          >
                            {a.draftVersion ? "Edit draft" : "Open"}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link
                            to="/w/$workspace/automations/runs"
                            params={params}
                            search={{ automationId: a.id }}
                          >
                            View runs
                          </Link>
                        </DropdownMenuItem>
                        {can("publish") && a.liveVersion && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => toggle.mutate(a)}>
                              {a.status === "paused" ? "Resume automation" : "Pause new runs"}
                            </DropdownMenuItem>
                          </>
                        )}
                        {can("edit_automations") && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setDetails({ name: a.name, description: a.description })
                                setRenaming(a)
                              }}
                            >
                              Rename…
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={duplicate.isPending}
                              onClick={() => duplicate.mutate(a)}
                            >
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => {
                                setConfirmText("")
                                setDeleting(a)
                              }}
                            >
                              Delete…
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })}
            {!filtered.length && (
              <TableRow>
                <TableCell colSpan={6} className="py-16 text-center">
                  <Search className="mx-auto mb-3 size-5 text-muted-foreground" />
                  {automations.length === 0
                    ? "No automations yet. Start with a template."
                    : "No automations match these filters."}
                  {automations.length > 0 && (
                    <Button
                      variant="link"
                      onClick={() => {
                        setQuery("")
                        setTab("all")
                        setChannel("all")
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="auto-template-callout">
        <div className="flex items-center gap-3">
          <span className="auto-callout-icon" aria-hidden="true">
            <Workflow className="size-5" />
          </span>
          <div>
            <p className="font-medium">Your next conversation starts with a template</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Proven starting points for replies, qualification and handover.
            </p>
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/automations/templates" params={params}>
            Explore templates
            <ArrowRight />
          </Link>
        </Button>
      </div>
      <Note>
        Draft changes stay separate from your published version. Pausing stops new runs; a human
        takeover also pauses the contact’s active automation.
      </Note>
      <Dialog open={renaming !== null} onOpenChange={(open) => !open && setRenaming(null)}>
        <DialogContent>
          <form
            className="contents"
            onSubmit={(e) => {
              e.preventDefault()
              if (renaming && details.name.trim())
                rename.mutate({
                  id: renaming.id,
                  name: details.name.trim(),
                  description: details.description.trim(),
                })
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename {renaming?.name}</DialogTitle>
              <DialogDescription>
                The name and description change everywhere at once; the published version keeps
                running.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-1.5">
                <Label htmlFor="rename-name">Name</Label>
                <Input
                  id="rename-name"
                  value={details.name}
                  maxLength={80}
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setDetails({ ...details, name: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="rename-description">Description</Label>
                <Textarea
                  id="rename-description"
                  rows={2}
                  value={details.description}
                  maxLength={200}
                  placeholder="What this journey is for"
                  onChange={(e) => setDetails({ ...details, description: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenaming(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!details.name.trim() || rename.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>
              {deleting?.status === "published"
                ? "It is still published. Pause new runs first, then delete."
                : "This removes the automation, its versions and its run history. Conversations a run still owns go back to your team. This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          {deleting?.status !== "published" && (
            <div className="grid gap-1.5">
              <Label htmlFor="confirm-delete">
                Type <span className="font-sans">delete</span> to confirm
              </Label>
              <Input
                id="confirm-delete"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoComplete="off"
                autoFocus
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Keep it
            </Button>
            {deleting?.status === "published" ? (
              can("publish") && (
                <Button
                  onClick={() => {
                    toggle.mutate(deleting)
                    setDeleting(null)
                  }}
                >
                  Pause new runs
                </Button>
              )
            ) : (
              <Button
                variant="destructive"
                disabled={confirmText.trim().toLowerCase() !== "delete" || remove.isPending}
                onClick={() => deleting && remove.mutate(deleting)}
              >
                Delete automation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
